import React, { useState, useEffect } from "react";
import styled from "styled-components";
import ReactDOM from "react-dom";
import AddActivityWindow from "../AddActivityWindow";
import ConfirmDialog from "../common/ConfirmDialog";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";

const CallWindowIcon = ({ icon, date, onAddActivity, isEditMode, style, setShowIcon, isWindowOpen, sessionData = null }) => {
    const [showActivity, setShowActivity] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const handleIconClick = () => {
        setShowActivity(true);
        if (isEditMode) {
            isWindowOpen.current = true;
        }
    };

    const handleClose = () => {
        setShowConfirm(true);
    };

    const handleConfirmClose = () => {
        setShowConfirm(false);
        closeSilently();
    };

    const closeSilently = () => {
        setShowActivity(false);
        if (isEditMode) {
            isWindowOpen.current = false;
            setShowIcon(false);
        }
    };

    const handleActivitySubmit = (activity) => {
        onAddActivity(activity);
        setShowActivity(false);
    };

    return (
        <>
            <CardIconWrapper onClick={handleIconClick}>
                <IconImage src={icon} alt={`${isEditMode ? 'Edit' : 'Add'} card icon`} style={style} />
            </CardIconWrapper>
            {showActivity && (
                <PopupContainer onClose={handleClose}>
                    <AddActivityWindow
                        onCancel={handleClose}
                        onCloseSilently={closeSilently}
                        date={date}
                        onSubmit={handleActivitySubmit}
                        isEditMode={isEditMode}
                        sessionData={sessionData}
                    />
                </PopupContainer>
            )}
            {showConfirm && (
                <ConfirmDialog
                    title="Discard Changes?"
                    message="Are you sure you want to cancel? Unsaved changes will be lost."
                    confirmText="Yes, Discard"
                    cancelText="No, Go Back"
                    variant="primary"
                    onConfirm={handleConfirmClose}
                    onCancel={() => setShowConfirm(false)}
                />
            )}
        </>
    );
};

const PopupContainer = ({ children, onClose }) => {
    useBodyScrollLock();

    const handleBackgroundClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => {
            window.removeEventListener('keydown', handleEsc);
        };
    }, [onClose]);

    return ReactDOM.createPortal(
        <PopupWrapper onClick={handleBackgroundClick}>{children}</PopupWrapper>,
        document.body
    );
};

const PopupWrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
`;

const CardIconWrapper = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    cursor: pointer;
`;

const IconImage = styled.img`
    width: 20px;
    height: 20px;
    object-fit: contain;
`;

export default CallWindowIcon;
